import { useQuery } from '@apollo/client';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  Badge,
  timestampFormatter,
  underscoreFormatter,
  warrantyStateColor,
} from '@retainerclub/common';
import { GET_WARRANTY } from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { Fragment } from 'react';

// Mapping functions for enum values to human-readable text
const mapScanStatus = (value) => {
  const mapping = {
    no_new_scan: 'No new scan after last retainer shipment',
    new_scan: 'New scan taken since last retainer shipment',
  };
  return mapping[value] || value;
};

const mapHaveCurrentSet = (value) => {
  if (value === true || value === 'true') {
    return 'Yes';
  }
  if (value === false || value === 'false') {
    return 'No';
  }
  return 'Not provided';
};

const mapHowOftenWear = (value) => {
  const mapping = {
    every_night: 'Every Night',
    a_few_times_per_week: 'A few times per week',
    daily: 'Daily (day and night)',
    as_needed: 'As needed',
    rarely: 'Rarely',
  };
  return mapping[value] || value;
};

const mapUserIssue = (value) => {
  const mapping = {
    does_not_fit: "Doesn't fit properly",
    manufacturing_issue: 'Manufacturing Issue / Defect',
  };
  return mapping[value] || value;
};

const mapArch = (value) => {
  const mapping = {
    upper: 'Top retainer',
    lower: 'Bottom retainer',
    both: 'Both retainers',
  };
  return mapping[value] || value;
};

const mapArchFit = (value) => {
  const mapping = {
    upper_good_fit: 'Good fit',
    upper_too_tight: 'Too tight',
    upper_too_loose: 'Too loose',
    upper_doesnt_cover_back_teeth: "Doesn't cover back teeth",
    upper_rubs_on_gums: 'Rubs on gums',
    upper_other: 'Other (Include specific notes in next section)',
    lower_good_fit: 'Good fit',
    lower_too_tight: 'Too tight',
    lower_too_loose: 'Too loose',
    lower_doesnt_cover_back_teeth: "Doesn't cover back teeth",
    lower_rubs_on_gums: 'Rubs on gums',
    lower_other: 'Other (Include specific notes in next section)',
  };
  return mapping[value] || value;
};

const mapWhereToShip = (value) => {
  const mapping = {
    partner_location: 'Orthodontic office',
    patient_address: "Patient's home address",
  };
  return mapping[value] || value;
};

// Humanizes the raw Warranty state enum. The previous local mapping only
// covered pending/approved/rejected plus three states that do not exist in
// Warranties::StateMachine, so every review state rendered with its underscores
// showing.
const mapWarrantyState = (value) => {
  if (!value) {
    return '—';
  }

  return underscoreFormatter(value);
};

function WarrantyReviewModal({ isOpen, onClose, warrantyId }) {
  const { data, loading, error } = useQuery(GET_WARRANTY, {
    variables: { id: warrantyId },
    skip: !(warrantyId && isOpen),
  });

  const warranty = data?.warranty;

  function getWarrantyDisplayData() {
    if (!warranty) {
      return null;
    }

    const patient = warranty.patient;
    const patientName = patient
      ? `${patient.firstName} ${patient.lastName}`
      : 'Not available';
    const orderId = warranty.productionOrder?.hexCode || 'Not available';
    const imageCount = warranty.warrantyImages?.length || 0;
    const photosUploaded = imageCount > 0 ? 'Yes' : 'No';

    return {
      patientName,
      orderId,
      photosUploaded,
    };
  }

  function renderLoadingModal() {
    return (
      <Transition.Root as={Fragment} show={isOpen}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500 text-sm">
                      Loading warranty details...
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  if (loading) {
    return renderLoadingModal();
  }

  function renderErrorModal() {
    return (
      <Transition.Root as={Fragment} show={isOpen}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="flex items-center justify-center py-8">
                    <div className="text-red-500 text-sm">
                      Error loading warranty details
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  if (error) {
    return renderErrorModal();
  }

  function renderMainModal() {
    if (!warranty) {
      return null;
    }

    const displayData = getWarrantyDisplayData();
    if (!displayData) {
      return null;
    }

    return (
      <Transition.Root as={Fragment} show={isOpen}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                  <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md text-gray-500 hover:bg-rcprimary-50 hover:text-rcprimary-600 focus:outline-none focus:ring-2 focus:ring-rcprimary-500 focus:ring-offset-2"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon aria-hidden="true" className="size-6" />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 w-full text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="font-semibold text-gray-900 text-lg leading-6"
                      >
                        PFG Review for case {warranty.hexCode?.toUpperCase()}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-gray-500 text-sm">
                          Review the submitted PFG (Patient Feedback Form)
                          details
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Request Summary */}
                  <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 text-left">
                    <div className="space-y-3">
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="font-bold text-gray-900 text-lg leading-6">
                          Request Summary
                        </h3>
                        <Badge
                          color={warrantyStateColor(warranty.state) || 'gray'}
                          name={mapWarrantyState(warranty.state)}
                          size="small"
                          withDot={false}
                        />
                      </div>
                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Patient:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {displayData.patientName}
                        </dd>
                      </div>

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Order ID:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {displayData.orderId}
                        </dd>
                      </div>

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Submitted:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {timestampFormatter(warranty.createdAt)}
                        </dd>
                      </div>

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Issue Type:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {mapUserIssue(warranty.userIssue) || 'Not provided'}
                        </dd>
                      </div>

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Affected Retainer:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {mapArch(warranty.arch) || 'Not provided'}
                        </dd>
                      </div>

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Recent 3D Scan:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {mapScanStatus(warranty.scanStatus) || 'Not provided'}
                        </dd>
                      </div>

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Current Retainers Available:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {mapHaveCurrentSet(warranty.haveCurrentSet)}
                        </dd>
                      </div>

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Wear Frequency:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {mapHowOftenWear(warranty.howOftenWear) ||
                            'Not provided'}
                        </dd>
                      </div>

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Shipping to:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {mapWhereToShip(warranty.whereToShip) ||
                            "Patient's home"}
                        </dd>
                      </div>

                      {/* Conditional arch fit information */}
                      {warranty.arch === 'upper' && (
                        <div className="flex">
                          <dt className="w-1/3 font-bold text-gray-900 text-sm">
                            Top Arch Issue:
                          </dt>
                          <dd className="text-gray-900 text-sm">
                            {mapArchFit(warranty.upperArchFit) ||
                              'Not provided'}
                          </dd>
                        </div>
                      )}

                      {warranty.arch === 'lower' && (
                        <div className="flex">
                          <dt className="w-1/3 font-bold text-gray-900 text-sm">
                            Bottom Arch Issue:
                          </dt>
                          <dd className="text-gray-900 text-sm">
                            {mapArchFit(warranty.lowerArchFit) ||
                              'Not provided'}
                          </dd>
                        </div>
                      )}

                      {warranty.arch === 'both' && (
                        <>
                          <div className="flex">
                            <dt className="w-1/3 font-bold text-gray-900 text-sm">
                              Top Arch Issue:
                            </dt>
                            <dd className="text-gray-900 text-sm">
                              {mapArchFit(warranty.upperArchFit) ||
                                'Not provided'}
                            </dd>
                          </div>
                          <div className="flex">
                            <dt className="w-1/3 font-bold text-gray-900 text-sm">
                              Bottom Arch Issue:
                            </dt>
                            <dd className="text-gray-900 text-sm">
                              {mapArchFit(warranty.lowerArchFit) ||
                                'Not provided'}
                            </dd>
                          </div>
                        </>
                      )}

                      <div className="flex">
                        <dt className="w-1/3 font-bold text-gray-900 text-sm">
                          Photos Uploaded:
                        </dt>
                        <dd className="text-gray-900 text-sm">
                          {displayData.photosUploaded}
                        </dd>
                      </div>

                      {/* Additional Details */}
                      {warranty.message && (
                        <div className="flex">
                          <dt className="w-1/3 font-bold text-gray-900 text-sm">
                            Additional Details:
                          </dt>
                          <dd className="text-gray-900 text-sm">
                            {warranty.message}
                          </dd>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes (partner-only notes when viewed by partner) */}
                  {warranty.warrantyNotes?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="mb-2 font-medium text-gray-700 text-sm">
                        Notes
                      </h3>
                      <ul className="space-y-2">
                        {[...warranty.warrantyNotes]
                          .sort(
                            (a, b) =>
                              new Date(b.createdAt) - new Date(a.createdAt),
                          )
                          .map((note) => (
                            <li
                              key={note.id}
                              className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                            >
                              <p className="text-gray-900">{note.message}</p>
                              <p className="mt-1 text-gray-500 text-xs">
                                {timestampFormatter(note.createdAt)}
                              </p>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {/* Success Message */}
                  <div className="mt-10 rounded-lg bg-green-50 p-4">
                    <div className="text-center">
                      <p className="text-green-800 text-sm">
                        Your request will be reviewed within 24 business hours
                        and processed for a remake delivery within 7-10 days
                        from submission if it meets the requirements. We will
                        reach out to the respective party if any additional
                        information is needed.
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  if (!warranty) {
    return null;
  }

  return renderMainModal();
}

WarrantyReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  warrantyId: PropTypes.string,
};

export default WarrantyReviewModal;
